/** @jsx jsx */
import { jsx, Box, ThemeUIStyleObject, ResponsiveStyleValue } from "theme-ui"
import React from "react"

import { PrimitiveProps } from "@styles/types"

const px = (n: string | number): string =>
  typeof n === "number" ? n + "px" : n

const widthToColumns = (
  width: ResponsiveStyleValue<string | number>,
  repeat?: ResponsiveStyleValue<string>
):
  | ResponsiveStyleValue<string | number>
  | ResponsiveStyleValue<string | number>[]
  | false => {
  if (Array.isArray(width)) {
    return width.map(w =>
      widthToColumns(w as string | number, repeat)
    ) as ResponsiveStyleValue<string | number>[]
  }

  return !!width && `repeat(auto-${repeat}, minmax(${px(width)}, 1fr))`
}

const countToColumns = (
  n: ResponsiveStyleValue<string | number>
):
  | ResponsiveStyleValue<string | number>
  | ResponsiveStyleValue<string | number>[]
  | false => {
  if (Array.isArray(n)) {
    return n.map(w =>
      countToColumns(w as string | number)
    ) as ResponsiveStyleValue<string | number>[]
  }

  return !!n && (typeof n === "number" ? `repeat(${n}, 1fr)` : n)
}

const spanPrefix = (
  v: ResponsiveStyleValue<number>
):
  | ResponsiveStyleValue<string | number>
  | ResponsiveStyleValue<string | number>[]
  | false => {
  if (Array.isArray(v)) {
    return v.map(_v =>
      spanPrefix(_v as number)
    ) as ResponsiveStyleValue<number>[]
  }

  return !!v && `span ${v}`
}

export interface GridProps
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"div"> {
  width?: ResponsiveStyleValue<string | number>
  columns?: ResponsiveStyleValue<string | number>
  rows?: ResponsiveStyleValue<string | number>
  gap?: ResponsiveStyleValue<string | number>
  repeat?: ResponsiveStyleValue<string>
  alignY?: ResponsiveStyleValue<string>
  alignX?: ResponsiveStyleValue<string>
}

export interface GridCellProps
  extends PrimitiveProps,
    React.ComponentPropsWithoutRef<"div"> {
  span?: ResponsiveStyleValue<number>
  spanY?: ResponsiveStyleValue<number>
  spanX?: ResponsiveStyleValue<number>
  offset?: ResponsiveStyleValue<number>
  offsetY?: ResponsiveStyleValue<number>
  offsetX?: ResponsiveStyleValue<number>
  alignY?: ResponsiveStyleValue<string>
  alignX?: ResponsiveStyleValue<string>
}

export const gridStyles: ThemeUIStyleObject = {
  display: "grid",
}

const Grid: React.FC<GridProps> = ({
  width,
  columns,
  rows,
  gap,
  repeat,
  alignY,
  alignX,
  children,
  sx,
  ...props
}) => {
  const gridTemplateColumns = (
    !!width
      ? widthToColumns(width, repeat)
      : !!columns && countToColumns(columns)
  ) as ResponsiveStyleValue<string>

  const gridTemplateRows = (!!rows &&
    countToColumns(rows)) as ResponsiveStyleValue<string>

  return (
    <Box
      sx={{
        ...gridStyles,
        gridTemplateColumns,
        ...(gridTemplateRows && { gridTemplateRows }),
        gridGap: gap,
        ...(alignY && { alignItems: alignY }),
        ...(alignX && { justifyItems: alignX }),
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  )
}

Grid.defaultProps = {
  gap: 3,
  repeat: "fit",
  columns: 12,
}

export const gridCellStyles: ThemeUIStyleObject = {}

const GridCell: React.FC<GridCellProps> = ({
  span,
  spanY,
  spanX,
  offset,
  offsetY,
  offsetX,
  alignY,
  alignX,
  sx,
  ...props
}) => {
  const gridColumnEnd = (
    !!span ? spanPrefix(span) : !!spanX ? spanPrefix(spanX) : null
  ) as ResponsiveStyleValue<string>

  const gridRowEnd = (
    !!spanY ? spanPrefix(spanY) : null
  ) as ResponsiveStyleValue<string>

  const gridColumnStart = !!offset
    ? (offset as number) + 1
    : offsetX
    ? (offsetX as number) + 1
    : null

  const gridRowStart = !!offsetY ? (offsetY as number) + 1 : null

  return (
    <Box
      sx={{
        ...gridCellStyles,
        ...(gridColumnStart && { gridColumnStart }),
        ...(gridColumnEnd && { gridColumnEnd }),
        ...(gridRowStart && { gridRowStart }),
        ...(gridRowEnd && { gridRowEnd }),
        ...(alignY && { alignSelf: alignY }),
        ...(alignX && { justifySelf: alignX }),
        ...sx,
      }}
      {...props}
    />
  )
}

export { Grid, GridCell }
